// ─── Cluster F — Polling Loop (~80 LoC) ───────────────────────────────────────
// Per docs/design/app-js-split-plan-260522.md §3 Cluster F / Module 7.
//
// Exports: startPolling, pollLoop.
//
// Imports from sibling split modules (Phases 1–4, already extracted):
//   globals-boot.mjs → API, POLL_INTERVAL
//   job-state.js     → jobState (Phase 10.5 class-shape retrofit, methods
//                       listJobIds, getJob, hasJob, removeJob, trackJob,
//                       saveJobIds, clearMonotonicCache), ACTIVE_STATUSES
//   banners.mjs      → updateSystemBanner
//
// Window bridge for phase not yet extracted (Phase 7 shim — remove on extraction):
//   window._adaRender — Phase 10 (renderers.mjs) render
//
// Globals (read-only at call time):
//   window.adatool?.log        — logging
//   window.MainJobStatus       — status-string constants (from main-job-status.js)
//   window.MAIN_JOB_TERMINAL_STATUSES — terminal-status Set (from main-job-status.js)

import { API, POLL_INTERVAL } from './globals-boot.mjs';
import { jobState, ACTIVE_STATUSES } from './job-state.js';
import { updateSystemBanner } from './banners.mjs';

// ─── Phase 7 bridge helper ─────────────────────────────────────────────────
// Called at runtime (not import-time) so boot order does not matter.
// Replace with a direct import from renderers.mjs in Phase 10.

function _render() {
    if (typeof window !== 'undefined' && typeof window._adaRender === 'function') {
        window._adaRender();
    }
}

// ─── Job-poll state machine (3-state, re-entrancy-safe) ───────────────────
// Replaces the previous boolean `polling` flag. The DRAINING state catches
// the re-entrancy window where two paths could both observe `!polling` in
// the same event-loop tick (one synchronous startPolling() call followed
// by another before the first pollLoop() returns control). DRAINING is a
// 1-tick cooldown that holds startPolling() from re-arming until the
// completing iteration has fully unwound.
//
// States:
//   IDLE     — no active poll loop; safe to start
//   ACTIVE   — pollLoop running; further startPolling() calls are no-ops
//   DRAINING — last iteration finishing; holds re-entrancy until the
//              ACTIVE -> IDLE drain completes (next event-loop tick)
const PollingState = Object.freeze({
    IDLE:     'IDLE',
    ACTIVE:   'ACTIVE',
    DRAINING: 'DRAINING',
});
/** @type {'IDLE' | 'ACTIVE' | 'DRAINING'} */
let _pollingState = PollingState.IDLE;

// ─── startPolling ──────────────────────────────────────────────────────────

export function startPolling() {
    // Re-entrancy guard: ACTIVE means pollLoop is mid-iteration, DRAINING
    // means it just decided to stop and the next-tick hand-off hasn't
    // settled. In both cases a fresh startPolling() is a no-op — the
    // existing loop will observe new active jobs on its next iteration,
    // OR (post-DRAIN) we'll reach IDLE and the caller's NEXT startPolling
    // attempt will succeed.
    if (_pollingState !== PollingState.IDLE) return;
    _pollingState = PollingState.ACTIVE;
    pollLoop();
}

// ─── pollLoop ─────────────────────────────────────────────────────────────

export async function pollLoop() {
    const activeIds = jobState.listJobIds().filter(id => {
        const job = jobState.getJob(id);
        return job ? ACTIVE_STATUSES.has(job.status) : false;
    });
    if (activeIds.length === 0) {
        window.adatool?.log?.info('[ada-tool] polling stopped: no active jobs');
        // DRAINING is a 1-tick cooldown — a second startPolling() inside
        // this same event-loop tick won't re-arm. We flip to IDLE on the
        // next macrotask so the loop can fully unwind first.
        _pollingState = PollingState.DRAINING;
        setTimeout(() => { _pollingState = PollingState.IDLE; }, 0);
        return;
    }

    for (const id of activeIds) {
        try {
            const job = await fetch(`${API}/api/jobs/${id}`).then(r => r.ok ? r.json() : null);
            if (job) {
                // Cancelled jobs vanish from the UI.
                if (job.status === window.MainJobStatus?.CANCELLED) {
                    window.adatool?.log?.info(`[ada-tool] job ${id} cancelled — removing from UI`);
                    jobState.removeJob(id);
                    jobState.clearMonotonicCache(id);
                    jobState.saveJobIds();
                    continue;
                }
                const prevJob = jobState.getJob(id);
                const prev = prevJob ? prevJob.status : undefined;
                // Route through jobState.trackJob — validates legal transitions
                // and merges server payload fields onto the existing entry,
                // preserving client-only state (size from upload) that the
                // server may omit.
                const updated = jobState.trackJob(job, { eventTs: Date.now() });
                if (updated && updated.status !== prev) {
                    window.adatool?.log?.info(`[ada-tool] job ${id} (${updated.filename}): ${prev} → ${updated.status}`);
                    if (updated.status === window.MainJobStatus?.FAILED) window.adatool?.log?.error(`job ${id} error:`, updated.error);
                    if (window.MAIN_JOB_TERMINAL_STATUSES?.has(updated.status)) {
                        jobState.clearMonotonicCache(id);
                    }
                }
            }
        } catch (e) {
            window.adatool?.log?.warn(`poll failed for ${id}:`, e instanceof Error ? e.message : String(e));
        }
    }

    try {
        const q = await fetch(`${API}/api/queue`).then(r => r.json());
        updateSystemBanner(q);
    } catch (e) {
        window.adatool?.log?.warn('queue fetch failed:', e instanceof Error ? e.message : String(e));
    }

    _render();
    setTimeout(pollLoop, POLL_INTERVAL);
}

// Re-export the jobState singleton so seam tests can reach the exact instance
// that pollLoop reads (module-identity safety when tsx remaps .js → .ts imports
// from TypeScript test files but NOT from .mjs source files).
export { jobState };
